/**
 * Calculator (pre-comparator simulation form) analytics → PostHog.
 *
 * Mirrors `comparatorAnalytics.ts` for the data-entry form that advisors fill in
 * BEFORE the comparator (mortgage + cash, 4 steps). Goes through the same
 * posthog-js SDK that `posthog.ts` initialises on advisor calculator routes; the
 * SDK handles autocapture + session recording (`maskAllInputs`), so this module
 * covers ONLY explicit funnel / field / friction events.
 *
 * PII safety: every event's properties pass through a strict allowlist
 * (`SAFE_CALCULATOR_PROPERTIES`). Anything not on the list is dropped before the
 * request leaves the browser. Crucially, field-level events carry ONLY metadata
 * about a field (its name, whether it was filled / changed) — NEVER the value —
 * so a client's birth date / income / address can never leak. Identification
 * happens in `posthog.ts` (advisor id), so the distinct id is never the client.
 */
import posthog from 'posthog-js';
import { hasPosthogApiKey, ensureCalculatorUserIdentified } from '@/plugins/posthog';

export enum CalculatorEvent {
  // Funnel
  STARTED = 'calculator_started',
  STEP_VIEWED = 'calculator_step_viewed',
  STEP_COMPLETED = 'calculator_step_completed',
  FIELD_COMPLETED = 'calculator_field_completed',
  OFFERS_REQUESTED = 'calculator_offers_requested',
  OFFERS_REQUEST_FAILED = 'calculator_offers_request_failed',
  // Friction
  STEP_BACK = 'calculator_step_back',
  VALIDATION_ERROR = 'calculator_validation_error',
}

const ORIGIN_LIB = 'calculator-analytics';
const ORIGIN_LIB_VERSION = '1';

/**
 * The ONLY property keys allowed to leave the browser. Add keys here
 * deliberately — never widen this to "everything" and never add an identity
 * field (firstName, lastName, email, phone, pesel, birthDate, address, …) or a
 * raw client value. Field events describe fields, they never carry field values.
 */
export const SAFE_CALCULATOR_PROPERTIES: ReadonlySet<string> = new Set([
  // Context / identifiers — join keys, not client PII
  'calculatorSessionId',
  'simulationId',
  'productType',
  'tenant',
  'loanPurpose',
  'source',
  // Step funnel
  'stepNumber',
  'stepName',
  'fromStep',
  'toStep',
  'maxStepReached',
  'stepsViewedCount',
  'stepsCompletedCount',
  'durationMs',
  // Field-level — metadata only, never the value
  'fieldName',
  'filled',
  'changed',
  'optional',
  'conditional',
  // Form completeness — correlate with comparator filter activity by simulationId
  'visibleFieldsCount',
  'engagedFieldsCount',
  // Structure counters
  'householdsCount',
  'borrowersCount',
  'hasAdditionalIncome',
  'hasLiabilities',
  // Errors
  'errorCode',
]);

type CalculatorPropertyValue = string | number | boolean | null;

const toSafePrimitiveValue = (value: unknown): CalculatorPropertyValue => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value === null || value === undefined) {
    return null;
  }
  return String(value);
};

export const sanitizeCalculatorProperties = (
  properties: Record<string, unknown>,
): Record<string, CalculatorPropertyValue> => {
  const safeProperties: Record<string, CalculatorPropertyValue> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (!SAFE_CALCULATOR_PROPERTIES.has(key)) {
      return;
    }
    safeProperties[key] = toSafePrimitiveValue(value);
  });

  return safeProperties;
};

/**
 * Send a calculator form analytics event. Properties are sanitized through the
 * allowlist; `$origin_lib` tags the source so events are filterable in PostHog.
 * Always resolves — never throws. Prefer calling via the `useCalculatorAnalytics`
 * composable, which injects calculatorSessionId / simulationId / productType.
 */
export const captureCalculatorEvent = (
  event: CalculatorEvent,
  rawProperties: Record<string, unknown> = {},
): void => {
  if (!hasPosthogApiKey()) {
    return;
  }

  // Catches the cold-reload race where AuthService.User landed AFTER the
  // route-driven identify in posthog.ts. Idempotent.
  ensureCalculatorUserIdentified();

  const properties: Record<string, unknown> = {
    ...sanitizeCalculatorProperties(rawProperties),
    $origin_lib: ORIGIN_LIB,
    $origin_lib_version: ORIGIN_LIB_VERSION,
  };

  posthog.capture(event, properties);
};
